"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { Mail, MapPin } from "lucide-react";
import LogoWithName from "./utils/logo_with_name";

const servicesList = [
  "Photo Retouching",
  "Background Removal",
  "Clipping Path",
  "Ghost Mannequin",
  "Color Correction",
  "Shadow Creation",
];

const quickLinks = [
  {
    label: "Home",
    link: "/",
  },
  {
    label: "Services",
    link: "#services",
  },
  {
    label: "How it Works",
    link: "#how-it-works",
  },
  {
    label: "Contact",
    link: "/contact",
  },
];

export function Footer() {
  return (
    <footer className="bg-zinc-950 border-t border-primary/30 text-zinc-400">
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="md:col-span-5"
          >
            <Link href="/" className="flex items-center mb-6">
              <LogoWithName size={28} />
            </Link>
            <p className="mb-8 leading-relaxed text-zinc-400 max-w-md">
              Professional image editing services that transform your photos
              into stunning visual masterpieces. Quality and speed in every
              project.
            </p>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="md:col-span-3"
          >
            <h4 className="text-lg font-semibold text-white mb-6">Services</h4>
            <ul className="space-y-3 text-sm">
              {servicesList?.map((service) => (
                <li key={service}>
                  <Link
                    href="#services"
                    className="hover:text-white transition-colors hover:underline"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="md:col-span-2"
          >
            <h4 className="text-lg font-semibold text-white mb-6">Company</h4>
            <ul className="space-y-3 text-sm">
              {quickLinks?.map((item) => (
                <li key={item.link}>
                  <Link
                    href={item.link}
                    className="hover:text-white transition-colors hover:underline"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="md:col-span-2"
          >
            <h4 className="text-lg font-semibold text-white mb-6">Contact</h4>
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-primary mt-0.5" />
                <a
                  href="mailto:hello@picoviyan.com"
                  className="hover:text-white transition-colors"
                >
                  hello@picoviyan.com
                </a>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary mt-0.5" />
                <span>Remote Team, Global Service</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="border-t border-primary/20 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center text-sm"
        >
          <p className="text-zinc-500">
            © {new Date().getFullYear()} PicOviyan. All rights reserved.
          </p>

          <div className="flex items-center gap-6 mt-4 md:mt-0">
            <Link
              href="/privacy-policy"
              className="hover:text-white transition-colors hover:underline"
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms-of-service"
              className="hover:text-white transition-colors hover:underline"
            >
              Terms of Service
            </Link>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}
